import React, { useState, useEffect } from "react";
import ProfileDetails from "./ProfileDetails/ProfileDetails";
import AddressDetails from "./ProfileDetails/AddressDetails";
import SocialMedia from "./ProfileDetails/SocialMedia";
import Documentation from "./ProfileDetails/Documentation";
import useProfileStore from "../../store/builders/useProfileStore";
import usePortfolioStore from "../../store/builders/usePortfolioStore";
import ServicesTile from "./ProjectServices/ServicesTile";
import PortfolioUpload from "./ProjectPortfolio/PortfolioTile";
import PortfolioDisplay from "./ProjectPortfolio/PortfolioDisplay";
import { toast } from "react-hot-toast";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const ProfileBanner = ({
  coverImageUrl,
  name: propName,
  profileImageUrl: propProfileImageUrl,
  role: propRole,
  location: propLocation,
  progress,
}) => {
  const [activeTab, setActiveTab] = useState("profile");
  const {
    profile: storeProfile,
    fetchProfile,
    isLoading: isStoreProfileLoading,
    updateProfile,
    isUpdating,
  } = useProfileStore();

  const {
    portfolio,
    isUploading: isPortfolioUploading,
    isDeleting: isPortfolioDeleting,
    fetchPortfolio
  } = usePortfolioStore();

  useEffect(() => {
    const loadData = async () => {
      await fetchProfile();
      await fetchPortfolio();
    };
    loadData();
  }, [fetchProfile, fetchPortfolio]);

  const builderDataFromStore = storeProfile?.builder;
  const displayCompanyName = builderDataFromStore?.company_name || propRole || "Company Name";
  const displayEngineerName = builderDataFromStore?.engineer_name || propName || "Engineer";
  const displayDistrict = builderDataFromStore?.district || propLocation || "Location";
  const displayProfileImage = builderDataFromStore?.profile_image || propProfileImageUrl || "/assets/Support.png";
  const imageKey = builderDataFromStore?.profile_image || Date.now();

  const handleProfileImageChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Client-side validation
      const maxFileSize = 5 * 1024 * 1024; // 5MB
      const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/jpg"];

      if (file.size > maxFileSize) {
        toast.error("File is too large. Maximum size is 5MB.");
        return;
      }
      if (!allowedTypes.includes(file.type)) {
        toast.error("Invalid file type. Please select a JPG, PNG, or WEBP image.");
        return;
      }

      // Call store action with the file
      const success = await updateProfile({ profileImage: file });

      // Force refetch profile to get updated image URL from server
      if (success) {
        await fetchProfile();
      }
    }
  };

  const tabs = [
    { id: "profile", label: "My Profile" },
    { id: "address", label: "Address" },
    { id: "documentation", label: "Documentation" },
    { id: "social", label: "Social Media" },
    { id: "services", label: "Services" },
    { id: "portfolio", label: "Portfolio" },
  ];

  return (
    <>
      <div className="bg-gray-100 overflow-hidden shadow rounded-2xl text-sm">
        {/* Profile Header - Mobile Responsive */}
        <div className="relative px-4 md:px-5 py-2 bg-white">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            {/* Profile Image */}
            <div className="flex-shrink-0 relative z-10">
              {isStoreProfileLoading && !builderDataFromStore ? (
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full border-2 border-white shadow-lg flex items-center justify-center bg-gray-200">
                  <div className="w-4 h-4 md:w-6 md:h-6 border-2 border-blue-500 border-solid border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : (
                <div className="relative">
                  <img
                    key={imageKey}
                    src={displayProfileImage}
                    alt={displayCompanyName}
                    className={`w-16 h-16 md:w-20 md:h-20 rounded-full border-2 border-white shadow-lg object-cover ${isUpdating ? 'filter brightness-75' : ''}`}
                    onError={(e) => {
                      e.target.src = "/assets/Support.png";
                    }}
                  />
                  {isUpdating && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                      <AiOutlineLoading3Quarters className="animate-spin text-lg md:text-2xl text-white" />
                    </div>
                  )}
                  {!isUpdating && (
                    <div
                      className="absolute -bottom-1 -right-1 md:bottom-12 md:-right-2 bg-white rounded-full p-1 shadow-lg cursor-pointer hover:bg-gray-100 transition"
                      onClick={() => document.getElementById("profile-image-input").click()}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3 w-3 md:h-4 md:w-4 text-gray-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                        />
                      </svg>
                    </div>
                  )}
                  <input
                    type="file"
                    id="profile-image-input"
                    className="hidden"
                    accept="image/jpeg,image/png,image/webp,image/jpg"
                    onChange={handleProfileImageChange}
                    disabled={isUpdating}
                  />
                </div>
              )}
            </div>

            {/* Profile Info */}
            <div className="flex-1 min-w-0">
              <h2 className="text-sm md:text-base font-semibold truncate">{displayCompanyName}</h2>
              <p className="text-gray-600 flex flex-col md:flex-row md:items-center md:space-x-2 text-xs md:text-sm">
                <span className="truncate">{displayEngineerName}</span>
                <span className="hidden md:inline">•</span>
                <span className="truncate">{displayDistrict}</span>
              </p>
              <div className="mt-2">
                <div className="h-1.5 bg-gray-200 rounded-full w-24 md:w-32">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${progress >= 96 ? "bg-green-500" : "bg-red-500"}`}
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-1">{progress}%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation - Unified for Mobile & Desktop */}
        <div className="relative bg-white pt-2">
          <ul className="flex justify-start gap-4 md:gap-6 text-gray-600 w-full px-4 text-sm overflow-x-auto no-scrollbar">
            {tabs.map((tab) => (
              <li
                key={tab.id}
                className={`cursor-pointer px-4 py-2 h-full transition-colors select-none whitespace-nowrap ${activeTab === tab.id ? "bg-gray-300 font-semibold" : "hover:bg-[#E0E0E0]"
                  }`}
                style={{ borderRadius: "10px 10px 0 0" }}
                onClick={() => setActiveTab(tab.id)}
              >
                <span>{tab.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Content Area */}
      <div className="mt-4 p-3 md:p-4 bg-white shadow-md rounded-2xl text-sm">
        {activeTab === "profile" && <ProfileDetails profile={storeProfile} />}
        {activeTab === "address" && <AddressDetails profile={storeProfile} />}
        {activeTab === "documentation" && (
          <Documentation profile={storeProfile} />
        )}
        {activeTab === "social" && <SocialMedia profile={storeProfile} />}
        {activeTab === "services" && <ServicesTile />}
        {activeTab === "portfolio" && (
          <div className="space-y-2">
            <PortfolioUpload />
            <div>
              <PortfolioDisplay portfolios={portfolio} isLoading={isPortfolioUploading || isPortfolioDeleting} />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ProfileBanner;